import { ApolloProvider } from '@apollo/client';
import { ModalProvider } from '@area2k/use-modal';
import { App as CapacitorApp } from '@capacitor/app';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { IonApp, IonContent, IonRefresher } from '@ionic/react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { useEffect, useState } from 'react';
import { ModalProvider as ReactModalHookProvider } from 'react-modal-hook';
import { Provider as ReduxProvider } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import LoadingState from './components/LoadingState';
import { StripeProvider } from './context';
import { PullToRefreshScreens } from './util/constants';
import { isWeb } from './util/platform';

import RouterDebug from '@/components/RouterDebug';
import Config from '@/config';
import PaymentMethodProvider from '@/hooks/usePaymentMethod';
import AppRouter from '@/routes';
import { store } from '@/store';
import * as googleanalytics from '@/util/analytics';
import client from '@/util/apollo/client';

import './index.css';

Sentry.init({
  dsn: Config.SENTRY_DSN,
  integrations: [new Integrations.BrowserTracing()],
  environment: Config.ENVIRONMENT,
  release: Config.BUILD_COMMIT,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.5,
});

googleanalytics.initializeGA(process.env.GA_TRACKING_ID);

const gb = new GrowthBook({
  apiHost: Config.GROWTHBOOK_API_HOST,
  clientKey: Config.GROWTHBOOK_CLIENT_KEY,
});

const App = () => {
  useEffect(() => {
    gb.loadFeatures()
      .then()
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    googleanalytics.logPageView(window.location.pathname);
  }, []);

  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const doRefresh = (event: CustomEvent) => {
    setRefreshing(true);
    window.location.reload();
    event.detail.complete();
  };

  useEffect(() => {
    if (!isWeb()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive && location.pathname === '/inappchat') {
          navigate(0);
        }
      });
      return () => {
        CapacitorApp.removeAllListeners();
      };
    }
  }, [location]);

  const GetSubComponents = () => {
    if (!isWeb()) {
      return SubComponentsWithoutStripe();
    } else {
      return <StripeProvider>{SubComponentsWithoutStripe()}</StripeProvider>;
    }
  };

  const disablePullToRefresh = () => {
    return (
      isWeb() ||
      (!PullToRefreshScreens.includesList.includes(location.pathname) &&
        PullToRefreshScreens.startsWithList.filter((l) =>
          location.pathname.startsWith(l)
        ).length === 0)
    );
  };

  const SubComponentsWithoutStripe = () => {
    return (
      <PaymentMethodProvider>
        <ModalProvider>
          <ReactModalHookProvider>
            <IonContent className="ion-content ion-padding" scrollEvents={true}>
              <IonRefresher
                disabled={disablePullToRefresh()}
                slot="fixed"
                onIonRefresh={doRefresh}
              >
                {refreshing ? <LoadingState text="Reloading..." /> : null}
              </IonRefresher>

              <RouterDebug />
              <AppRouter />
            </IonContent>
          </ReactModalHookProvider>
        </ModalProvider>
      </PaymentMethodProvider>
    );
  };

  return (
    <IonApp>
      <ApolloProvider client={client}>
        <ReduxProvider store={store}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <GrowthBookProvider growthbook={gb}>
              {GetSubComponents()}
            </GrowthBookProvider>
          </LocalizationProvider>
        </ReduxProvider>
      </ApolloProvider>
    </IonApp>
  );
};

export default App;
